import { API, SequenceMigrationPreview } from "@swivl-mono/api-helper";
import { filterClasses } from "@swivl-mono/helpers";
import { BotSequenceType } from "@swivl-mono/types";
import { Spinner } from "@swivl-mono/ui";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";

import { SequenceTypeSection } from "./SequenceTypeSection";
import { StepApplyBar, useStepApply } from "./StepApplyBar";
import { MigrationStepHandle, StepApplyOutcome } from "./workflow";

interface SequencesTabProps {
  orgId: number;
  /** Called after this step writes, so the workflow can refresh steps that read what it wrote. */
  onApplied: () => void;
}

/**
 * The Sequences step (ENG-3739): the account's legacy sequences, read from its own quests, as the
 * new engine will hold them. Everything is written as Simulated, so Snowy builds every message and
 * sends none; going live later is legacy off, then the org's mode On (ENG-3512).
 */
export const SequencesTab = forwardRef<MigrationStepHandle, SequencesTabProps>(function SequencesTab({ orgId, onApplied }, ref) {
  const [preview, setPreview] = useState<SequenceMigrationPreview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Set<BotSequenceType>>(new Set());

  const reload = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const p = await API.Cortex.previewSequenceMigration(orgId);
      setPreview(p);
      // Default: every type with something to write.
      setSelected(new Set(p.types.filter((t) => (t.status === "ready" || t.status === "off") && t.writeCount > 0).map((t) => t.sequenceType)));
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    reload().catch(() => {
      /* handled in reload */
    });
  }, [reload]);

  const handleToggleSelected = useCallback((type: BotSequenceType) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }, []);

  const writeCount = useMemo(
    () => (preview ? preview.types.filter((t) => selected.has(t.sequenceType)).reduce((sum, t) => sum + t.writeCount, 0) : 0),
    [preview, selected]
  );

  const handleApply = useCallback(async (): Promise<StepApplyOutcome> => {
    const res = await API.Cortex.applySequenceMigration(orgId, [...selected]);
    onApplied();
    await reload();
    const parts = [`${res.written + res.schedulesWritten} row(s) written`];
    if (res.removed > 0) parts.push(`${res.removed} removed to inherit`);
    if (res.kept > 0) parts.push(`${res.kept} kept as set in Snowy`);
    return {
      message: parts.join(", "),
      warning: res.failed > 0 ? `${res.failed} row(s) couldn't be written: ${res.failures.slice(0, 3).join("; ")}` : undefined
    };
  }, [orgId, selected, onApplied, reload]);

  useImperativeHandle(ref, () => ({ reload }), [reload]);
  const applyState = useStepApply(handleApply);

  if (loadError) {
    return <div className="body-sm p-6 text-red-500">Failed to load the sequences preview: {loadError}</div>;
  }
  if (loading || !preview) {
    return (
      <div className="body-sm flex items-center gap-2 p-6 text-grey-400">
        <Spinner /> Reading the account&apos;s legacy sequences…
      </div>
    );
  }

  const engineReady =
    preview.engine.sequencesPage && preview.engine.engine && preview.engine.reconciler && preview.engine.scheduler && preview.engine.dispatcher;
  const notes: { dot: string; text: string }[] = [];
  if (!engineReady) {
    notes.push({
      dot: "bg-yellow-500",
      text: "To review the result in Property Manager - NEW > Sequences, this account needs the Sequences page flag and the engine's master, Reconciler, Scheduler and Dispatcher flags."
    });
  }
  for (const flag of preview.flags) notes.push({ dot: "bg-yellow-500", text: flag.message });

  return (
    <>
      <StepApplyBar
        description={`${writeCount} row(s) to write for the ${selected.size} selected sequence type(s). The mode is Simulated, set at the org.`}
        applyLabel="Apply Sequences"
        confirmText={`This writes the selected sequence types to the new engine for org ${orgId}, always as Simulated: Snowy builds every message and sends none. It also removes rows this step wrote earlier that are no longer needed, so those scopes inherit again; rows edited in Snowy are never touched. Safe to re-run until cutover. To go live later: re-run this step, review in Property Manager - NEW > Sequences, turn legacy off in Settings > Sequences, then set the org's mode to On.`}
        disabled={writeCount === 0}
        disabledReason="Nothing to write for the selected types."
        state={applyState}
      />
      <div className="mx-auto max-w-5xl p-6">
        <div className="mb-4">
          <h2 className="heading-base">Sequences to migrate</h2>
          <p className="body-sm mt-1 text-grey-500">
            Read from this account&apos;s own quests. Only days that are on in legacy migrate, all as Simulated: the mode is set at the org, and a
            location with nothing on is written as Off.
            {preview.lastRun && <> Last applied {new Date(preview.lastRun.at).toLocaleString()}.</>}
          </p>
        </div>

        {notes.length > 0 && (
          <div className="mb-6 flex flex-col gap-2 rounded border border-grey-200 bg-white p-3">
            {notes.map((note) => (
              <div key={note.text} className="body-sm flex items-start gap-2 text-grey-700">
                <span className={filterClasses("mt-1.5 h-2 w-2 shrink-0 rounded-full", note.dot)} />
                <span>{note.text}</span>
              </div>
            ))}
          </div>
        )}

        {preview.types.map((type) => (
          <SequenceTypeSection
            key={type.sequenceType}
            type={type}
            selected={selected.has(type.sequenceType)}
            onToggleSelected={handleToggleSelected}
          />
        ))}
      </div>
    </>
  );
});
